// Saved views as the server keeps them (internal/report/view.go): what the screen
// needs to redraw a layout or a Tickets filter, never the branch or the dates.
import type { BandSpec, TicketViewFilters, ViewDraft, ViewSpec } from '../lib/types'
import { validBands } from './insightBands'
import { PERIODS, isTimed, validLayout, type Layout, type MeasureId, type PeriodId, type Visual } from './insightCalc'
import type { TicketFilters } from './ticketReport'

/** What a view sets on the screen. A missing period keeps the one chosen. */
export type ViewState = { layout: Layout; period: PeriodId; bands: BandSpec; visual: Visual; tickets: TicketViewFilters }

const KEYS = new Set(['layout', 'period', 'bands', 'visual', 'tickets'])
const SAVED_PERIODS = new Set<string>(PERIODS.map(([p]) => p).filter((p) => p !== 'custom'))

const keptPeriod = (p: PeriodId | undefined) => (p && SAVED_PERIODS.has(p) ? p : '')

/** A Figures view: the layout, the period (never dates), the bands and the visual opened. */
export function toSpec(s: { layout: Layout; period?: PeriodId; bands?: BandSpec; visual?: Visual | null }): ViewSpec {
  const layout = {
    rows: [...s.layout.rows],
    cols: [...s.layout.cols],
    values: s.layout.values.map((v) => (v.stat && isTimed(v.measure) ? { measure: v.measure, stat: v.stat } : { measure: v.measure })),
  }
  return {
    layout,
    period: keptPeriod(s.period),
    ...(s.bands ? { bands: { waitBy: s.bands.waitBy, waitEdges: [...s.bands.waitEdges], handlingEdges: [...s.bands.handlingEdges] } } : {}),
    ...(s.visual ? { visual: s.visual } : {}),
  }
}

/** A Tickets view: the tab's filters without branches and dates, only those set. */
export function ticketsSpec(s: { filters: TicketFilters; period?: PeriodId; names?: Partial<Record<keyof TicketFilters, string>> }): ViewSpec {
  const { branches: _b, from: _f, to: _t, ...rest } = s.filters
  const tickets: TicketViewFilters = {}
  for (const [k, v] of Object.entries(rest)) {
    if (v === undefined || v === '' || v === 0 || v === false || (Array.isArray(v) && v.length === 0)) continue
    ;(tickets as Record<string, unknown>)[k] = Array.isArray(v) ? [...v] : typeof v === 'object' ? { ...v } : v
  }
  const names = Object.entries(s.names ?? {}).filter(([k, v]) => v && NAMED.has(k) && (tickets as Record<string, unknown>)[k])
  if (names.length > 0) tickets.names = Object.fromEntries(names) as Record<string, string>
  return { period: keptPeriod(s.period), tickets }
}

const NAMED = new Set(['service', 'staff', 'arrival', 'desk', 'outcome', 'mark', 'booked'])

const STRING_FILTERS = new Set(['service', 'staff', 'result', 'channel', 'arrival', 'priority', 'language', 'issuedFrom', 'issuedTo', 'call', 'counted', 'desk', 'outcome', 'mark', 'booked'])

function validTickets(x: unknown): TicketViewFilters | null {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return null
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(x)) {
    if (STRING_FILTERS.has(k)) {
      if (typeof v !== 'string' || v.length > 200) return null
      out[k] = v
    } else if (k === 'sources') {
      if (!Array.isArray(v) || !v.every((s) => typeof s === 'string')) return null
      out[k] = [...v]
    } else if (k === 'ended') {
      if (v !== true) return null
      out[k] = true
    } else if (k === 'weekday') {
      if (!Number.isInteger(v) || (v as number) < 0 || (v as number) > 7) return null
      out[k] = v
    } else if (k === 'wait') {
      const w = v as Record<string, unknown> | null
      if (!w || typeof w !== 'object' || (w.by !== 'minutes' && w.by !== 'target') || !Number.isInteger(w.lo) || !Number.isInteger(w.hi)) return null
      if (Object.keys(w).some((wk) => !['by', 'lo', 'hi', 'notCalled'].includes(wk)) || (w.notCalled !== undefined && typeof w.notCalled !== 'boolean')) return null
      out[k] = { by: w.by, lo: w.lo, hi: w.hi, ...(w.notCalled ? { notCalled: true } : {}) }
    } else if (k === 'names') {
      if (!v || typeof v !== 'object' || Array.isArray(v)) return null
      if (Object.entries(v).some(([nk, nv]) => !NAMED.has(nk) || typeof nv !== 'string' || nv.length > 200)) return null
      out[k] = { ...v }
    } else return null
  }
  return out as TicketViewFilters
}

/** What a stored view sets, or null for one this screen cannot redraw. */
export function fromSpec(spec: unknown): Partial<ViewState> | null {
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return null
  const s = spec as Record<string, unknown>
  if (Object.keys(s).some((k) => !KEYS.has(k))) return null
  const out: Partial<ViewState> = {}
  if (s.period !== undefined && s.period !== '') {
    if (typeof s.period !== 'string' || !SAVED_PERIODS.has(s.period)) return null
    out.period = s.period as PeriodId
  }
  if ((s.layout === undefined) === (s.tickets === undefined)) return null
  if (s.tickets !== undefined) {
    if (s.bands !== undefined || (s.visual !== undefined && s.visual !== '')) return null
    const tickets = validTickets(s.tickets)
    if (!tickets) return null
    return { ...out, tickets }
  }
  const layout = validLayout(s.layout)
  if (!layout || layout.values.some((v) => v.stat && !isTimed(v.measure as MeasureId))) return null
  out.layout = layout
  if (s.bands !== undefined) {
    const bands = validBands(s.bands)
    if (!bands) return null
    out.bands = bands
  }
  if (s.visual !== undefined && s.visual !== '') {
    if (s.visual !== 'waits') return null
    out.visual = 'waits'
  }
  return out
}

/** The views this browser kept before views moved to the server, as private Figures views. */
export function localDrafts(x: unknown): ViewDraft[] {
  if (!Array.isArray(x)) return []
  return x.flatMap((v) => {
    const layout = validLayout(v?.layout)
    const name = typeof v?.name === 'string' ? v.name.trim().slice(0, 60) : ''
    if (!layout || !name || typeof v.id !== 'string' || !v.id || v.id.length > 100) return []
    const spec = toSpec({ layout, period: v.period, visual: v.visual === 'waits' ? 'waits' : null })
    return [{ name, kind: 'figures' as const, shared: false, spec, importKey: v.id }]
  })
}

/** A save the server refused for the view itself (400, 413, 422): asking again will not help. */
export function refusedView(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status
  return status === 400 || status === 413 || status === 422
}

/**
 * Moves this browser's views to the server: each saved once, in order, and the
 * browser's copy cleared once every one is saved or refused. A view the server
 * refuses is skipped and counted, and the rest still move; any other failure
 * (offline, signed out, the server down) stops it and keeps the copy for the next
 * visit. Each carries its browser id as importKey, which the server keeps once, so
 * a retry or a second tab saves no copy.
 */
export async function migrateLocal(
  read: () => unknown,
  save: (v: ViewDraft) => Promise<void>,
  clear: () => void,
): Promise<{ moved: number; skipped: number }> {
  const raw = read()
  const drafts = localDrafts(raw)
  let moved = 0
  let skipped = 0
  for (const d of drafts) {
    try {
      await save(d)
      moved++
    } catch (err) {
      if (!refusedView(err)) return { moved, skipped }
      skipped++
    }
  }
  if (raw != null) clear()
  return { moved, skipped }
}

function canonical(x: unknown): string {
  if (Array.isArray(x)) return '[' + x.map(canonical).join(',') + ']'
  if (x && typeof x === 'object')
    return (
      '{' +
      Object.keys(x)
        .filter((k) => (x as Record<string, unknown>)[k] !== undefined)
        .sort()
        .map((k) => JSON.stringify(k) + ':' + canonical((x as Record<string, unknown>)[k]))
        .join(',') +
      '}'
    )
  return JSON.stringify(x)
}

/** Whether two specs say the same, whatever order their fields came in. */
export const sameSpec = (a: ViewSpec, b: ViewSpec) => canonical(a) === canonical(b)
